import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCheck } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

/** Serien-Bestätigen: alle grünen Belege (alle Prüfungen bestanden) auf einen Klick bestätigen. */
export function isGreenReady(it) {
  const ex = it.extracted || {};
  const checks = it.checks;
  return it.status === "pending" && (it.kind === "receipt" || it.kind === "bill") && checks?.status === "green"
    && !checks?.requires_reverse_charge_decision && !ex.is_credit_note && !it.duplicate_warning;
}

export function BulkGreenConfirmButton({ companyId, items, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ready = items.filter(isGreenReady);
  if (ready.length === 0) return null;
  const sum = ready.reduce((s, it) => s + Math.abs(Number(it.extracted?.total_amount || 0)), 0);
  const skippedCredit = items.filter((it) => it.status === "pending" && it.checks?.status === "green" && it.extracted?.is_credit_note).length;

  const run = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/bulk-confirm`, { item_ids: ready.map((i) => i.id), green_only: true, book: false });
      const n = r.data?.count || 0;
      const failed = r.data?.failed || [];
      toast.success(`${n} Beleg${n === 1 ? "" : "e"} bestätigt — bereit zum Buchen${failed.length ? ` · ${failed.length} übersprungen` : ""}`, { duration: 6000 });
      failed.slice(0, 3).forEach((f) => toast.warning(f.error || "Bestätigung fehlgeschlagen", { duration: 8000 }));
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Serien-Bestätigung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid="inbox-bulk-green-button"
        title="Alle Belege, bei denen alle Prüfungen grün sind, auf einen Klick bestätigen (Gutschriften und Duplikate bleiben zur Einzelprüfung)">
        <CheckCheck className="h-3.5 w-3.5 mr-1.5" />Alle grünen bestätigen ({ready.length})
      </Button>
      <Dialog open={open} onOpenChange={(o) => !o && !busy && setOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="inbox-bulk-green-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCheck className="h-5 w-5 text-emerald-600" />{ready.length} grüne Belege bestätigen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>Alle Prüfungen bestanden – Lieferant, Betrag, Datum, Konto und MWST sind plausibel. Die Belege werden bestätigt und landen in der Belegliste <b>bereit zum Buchen</b> (noch keine Buchung).</p>
            <ul className="max-h-56 overflow-auto rounded-lg border border-slate-200 divide-y text-xs" data-testid="inbox-bulk-green-list">
              {ready.map((it) => (
                <li key={it.id} className="flex justify-between px-3 py-1.5">
                  <span className="truncate">{it.extracted?.supplier_name || it.file_name}</span>
                  <span className="font-mono ml-3 shrink-0">{it.extracted?.currency || "CHF"} {Math.abs(Number(it.extracted?.total_amount || 0)).toFixed(2)}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-between text-xs text-slate-500">
              <span>{skippedCredit > 0 ? `${skippedCredit} Gutschrift${skippedCredit === 1 ? "" : "en"} bleibt zur Einzelprüfung` : "Gutschriften und Duplikate werden nicht automatisch bestätigt"}</span>
              <span className="font-mono">Σ {sum.toFixed(2)}</span>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy} data-testid="inbox-bulk-green-cancel">Abbrechen</Button>
            <Button onClick={run} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="inbox-bulk-green-confirm">
              {busy ? "Bestätige…" : `${ready.length} bestätigen`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
